import React, { useEffect, useState } from "react";
import { Button, Stack, Typography, useTheme } from "@mui/material";
import { Icon } from "@iconify/react";
import { FormProvider, RHFTextField } from "@app/components";
import { Strings } from "@app/constants";
import { useResponsive, useRequestState } from "@app/hooks";
import { useDispatch } from "react-redux";
import { selectBrandState } from "@app/modules/categories/selector";
import { brand } from "@app/modules/categories/slice";
import { SellCarFormProps } from "../../types";
import { LoadingButton } from "@mui/lab";
import { useNavigate } from "react-router-dom";
import { PATH_DASHBOARD } from "@app/routes/paths";

const SellCarForm = ({ methods, onClose }: SellCarFormProps) => {
  const isMobile = useResponsive("down", "md");
  const theme = useTheme();
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();

  const { data } = useRequestState({
    stateSelector: selectBrandState,
  });

  useEffect(() => {
    dispatch(brand());
  }, [dispatch]);

  const filteredBrands = data?.filter((item: any) => {
    if (searchTerm === "") return true;
    return item?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase());
  });

  const brandId = methods.watch("brandId");

  const handleSellButton = () => {
    const currentValues = methods.getValues();
    navigate(PATH_DASHBOARD.sellCar, {
      state: {
        formValue: currentValues,
      },
    });
    onClose();
  };

  return (
    <FormProvider methods={methods}>
      <Stack
        spacing={isMobile ? 2 : 3}
        mt={2}
        alignItems="center"
        sx={{ width: "100%" }}
      >
        <Stack sx={{ width: isMobile ? "100%" : "85%" }}>
          <Typography
            sx={{
              color: theme.palette.common.black,
              fontFamily: "Work Sans",
              fontSize: 13,
              fontWeight: "500",
              mb: 0.5,
            }}
          >
            {Strings.authPopup.carsRegistrationsNumber}
          </Typography>
          <RHFTextField
            name="carNumber"
            inputProps={{
              style: { textTransform: "uppercase" },
            }}
            fullWidth
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: theme.palette.grey[200],
                borderRadius: 1.5,
                "& fieldset": { border: "none" },
              },
            }}
          />
        </Stack>

        <Typography fontWeight={600} sx={{ fontSize: isMobile ? 14 : 16 }}>
          {Strings.authPopup.selectCarsBrands}
        </Typography>

        <Stack
          direction={isMobile ? "column" : "row"}
          spacing={1}
          alignItems={isMobile ? "stretch" : "center"}
          sx={{ width: isMobile ? "100%" : "85%" }}
        >
          <RHFTextField
            name="searchBrand"
            placeholder="Search"
            value={searchTerm}
            onChange={(e: any) => setSearchTerm(e.target.value)}
            fullWidth
            InputProps={{
              startAdornment: (
                <Icon
                  icon="mdi:magnify"
                  width={20}
                  height={20}
                  style={{ marginRight: 8 }}
                />
              ),
              endAdornment: !isMobile && (
                <Button
                  variant="contained"
                  size="small"
                  sx={{
                    borderRadius: "5px",
                    textTransform: "none",
                    ml: 1,
                    px: 4,
                    py: 2.5,
                  }}
                >
                  {Strings.button.search}
                </Button>
              ),
            }}
          />
          {isMobile && (
            <Button
              variant="contained"
              size="medium"
              sx={{
                borderRadius: "6px",
                textTransform: "none",
                width: "100%",
                mt: 1,
                py: 1.5,
              }}
            >
              {Strings.button.search}
            </Button>
          )}
        </Stack>

        <Stack
          direction="row"
          flexWrap="wrap"
          justifyContent={isMobile ? "space-between" : "center"}
          sx={{ width: "100%", gap: 2, mt: 2 }}
        >
          {filteredBrands?.map((item: any) => {
            const isSelected = brandId === item?.id;
            return (
              <Stack
                key={item.id}
                onClick={() => methods.setValue("brandId", item.id)}
                spacing={1}
                alignItems="center"
                sx={{
                  flexBasis: isMobile ? "48%" : 100,
                  cursor: "pointer",
                  border: "2px solid",
                  borderColor: isSelected
                    ? theme.palette.primary.main
                    : "transparent",
                  borderRadius: 2,
                  overflow: "hidden",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "scale(1.05)",
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                  },
                }}
              >
                <img
                  src={item.image}
                  alt={item.name}
                  loading="lazy"
                  style={{
                    width: "100%",
                    height: 85,
                    borderRadius: 8,
                    objectFit: "cover",
                    opacity: isSelected ? 0.9 : 1,
                  }}
                />
                <Typography
                  sx={{
                    fontFamily: "Work Sans",
                    fontSize: isMobile ? 13 : 15,
                    textAlign: "center",
                    color: isSelected
                      ? theme.palette.primary.main
                      : theme.palette.text.primary,
                  }}
                >
                  {item.name}
                </Typography>
              </Stack>
            );
          })}
        </Stack>
      </Stack>
      <Stack
        direction={isMobile ? "column" : "row"}
        spacing={2}
        justifyContent="flex-end"
        sx={{ pt: 3 }}
      >
        <Button
          variant="outlined"
          onClick={() => onClose()}
          sx={{
            px: isMobile ? 2 : 6,
            py: 1.8,
            width: isMobile ? "100%" : "auto",
          }}
        >
          {Strings.button.cancel}
        </Button>
        <LoadingButton
          variant="contained"
          onClick={handleSellButton}
          sx={{
            px: isMobile ? 2 : 6,
            py: 1.8,
            width: isMobile ? "100%" : "auto",
          }}
        >
          {Strings.button.continue}
        </LoadingButton>
      </Stack>
    </FormProvider>
  );
};

export { SellCarForm };
